import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MatSelect } from '@angular/material';
import { DomSanitizer } from '@angular/platform-browser';
import { NgxSpinnerService } from 'ngx-spinner';
import { sessionConfirmDialogComponent } from '@fuse/components/confirm-dialog-session/confirm-dialog-session.component';
import { FuseConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';
import { SelectJobDialogComponent } from '@fuse/components/select-job-dialog/select-job-dialog.component';
import { WarehouseDocumentPreviewDialogComponent } from '@fuse/components/warehouse-document-preview-dialog/warehouse-document-preview-dialog.component';
import { FuseConfigService } from '@fuse/services/config.service';
import { APPCONFIG } from 'app/main/config';
import { COMMON_UTIL } from 'app/main/common_util';
import { WarehouseDeliveryDocumentApiService } from './warehouse-delivery-document-api.service';
import { PendingWarehouseFile, WarehouseDocumentType, WarehouseFileMetadata, WarehouseJobInfo } from './models';

const MAX_FILES_PER_BATCH = 10;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_BATCH_SIZE_BYTES = 40 * 1024 * 1024;
const DEFAULT_DOCUMENT_TYPE = 'PIC';

@Component({
    selector: 'app-warehouse-delivery-documents',
    templateUrl: './warehouse-delivery-documents.component.html?v=${APPCONFIG.cacheVersion}',
    styleUrls: ['./warehouse-delivery-documents.component.scss?v=${APPCONFIG.cacheVersion}']
})
export class WarehouseDeliveryDocumentsComponent implements OnInit, OnDestroy {
    @ViewChild('captureFileInput') captureFileInputVar: ElementRef;
    @ViewChild('uploadFileInput') uploadFileInputVar: ElementRef;
    @ViewChild('documentTypeSelect') documentTypeSelect: MatSelect;

    readonly main_app_logo = APPCONFIG.main_app_logo;

    identified = false;
    profileForm: FormGroup;

    job: WarehouseJobInfo;
    allDocumentTypes: WarehouseDocumentType[] = [];
    documentTypeFilterCtrl = new FormControl();
    selectedDocumentType: number = null;
    accessToken: string = null;

    storedFiles: WarehouseFileMetadata[] = [];
    pendingFiles: PendingWarehouseFile[] = [];
    /** Files read but not yet pushed to pendingFiles; counts toward the batch cap. */
    private inFlightReads = 0;

    loadingFiles = false;
    submitting = false;

    upperListColspan = 4;
    lowerListColspan = 2;
    upperListRowHeight = '5:1';
    lowerListRowHeight = '6:1';

    private confirmDialogRefSession: MatDialogRef<sessionConfirmDialogComponent>;
    private confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;
    private selectJobDialog: MatDialogRef<SelectJobDialogComponent>;
    private previewDialog: MatDialogRef<WarehouseDocumentPreviewDialogComponent>;

    constructor(
        public dialog: MatDialog,
        private api: WarehouseDeliveryDocumentApiService,
        private spinner: NgxSpinnerService,
        private _fuseConfigService: FuseConfigService,
        private _formBuilder: FormBuilder,
        private dateAdapter: DateAdapter<Date>,
        private domSanitizer: DomSanitizer
    ) {
        this._fuseConfigService.config = {
            layout: {
                navbar: { hidden: true },
                toolbar: { hidden: true },
                footer: { hidden: true }
            }
        };
        this.dateAdapter.setLocale('en-GB');
    }

    ngOnInit(): void {
        this.onForm();
        this.updateGridLayout(window.innerWidth);
    }

    ngOnDestroy(): void {
        this.revokeAllPendingPreviews();
    }

    onResize(event): void {
        this.updateGridLayout(event.target.innerWidth);
    }

    updateGridLayout(innerWidth): void {
        this.upperListRowHeight = '5:1';
        this.lowerListRowHeight = '6:1';
        this.upperListColspan = 4;
        this.lowerListColspan = 2;
        if (innerWidth <= 410) {
            this.upperListColspan = 1;
            this.lowerListColspan = 1;
            this.lowerListRowHeight = '4:1';
        } else if (innerWidth > 410 && innerWidth <= 550) {
            this.upperListColspan = 2;
            this.lowerListColspan = 1;
            this.upperListRowHeight = '3:1';
            this.lowerListRowHeight = '5:1';
        } else if (innerWidth > 550 && innerWidth <= 800) {
            this.upperListColspan = 2;
            this.lowerListColspan = 1;
            this.upperListRowHeight = '4:1';
            this.lowerListRowHeight = '6:1';
        } else if (innerWidth > 800 && innerWidth <= 1200) {
            this.upperListRowHeight = '3:1';
            this.lowerListRowHeight = '5:1';
        }
    }

    onForm(): void {
        this.profileForm = this._formBuilder.group({
            shipmentNo: ['', Validators.required],
            plantCode: ['', Validators.required],
            jobPkId: ['']
        });
    }

    // ---------------------------------------------------------------- Identify

    identify(): void {
        this.spinner.show();
        const value = this.profileForm.value;
        this.api.identify(value.shipmentNo, value.plantCode, value.jobPkId).subscribe(
            response => {
                this.spinner.hide();
                if (response.jobList) {
                    this.promptJobSelection(response.jobList);
                    return;
                }
                this.job = response.job;
                this.allDocumentTypes = response.documentTypes || [];
                this.accessToken = response.accessToken;
                this.identified = true;
                this.selectDefaultDocumentType();
            },
            err => {
                this.spinner.hide();
                this.handleApiError(err, 'Unable to identify the job. Please try again.', false);
            }
        );
    }

    private selectDefaultDocumentType(): void {
        const pic = this.allDocumentTypes.find(t => (t.documentType || '').trim().toUpperCase() === DEFAULT_DOCUMENT_TYPE);
        if (pic) {
            this.commitDocumentTypeChange(pic.documentId);
        }
    }

    private promptJobSelection(jobList): void {
        this.selectJobDialog = this.dialog.open(SelectJobDialogComponent, { panelClass: 'select-job-dialog-panel' });
        this.selectJobDialog.componentInstance.config.title = 'Select';
        this.selectJobDialog.componentInstance.config.msg =
            'This shipment has multiple deliveries for the given postcode, please select one.';
        this.selectJobDialog.componentInstance.config.jobs = jobList;

        this.selectJobDialog.afterClosed().subscribe(result => {
            if (result && result.selected) {
                this.profileForm.controls['jobPkId'].setValue(result.jobPkId);
                this.identify();
            }
        });
    }

    backToIdentify(): void {
        this.revokeAllPendingPreviews();
        this.identified = false;
        this.job = undefined;
        this.allDocumentTypes = [];
        this.selectedDocumentType = null;
        this.accessToken = null;
        this.storedFiles = [];
        this.pendingFiles = [];
        this.onForm();
    }

    // ---------------------------------------------------------------- Document type selection

    onDocumentTypeSelected(newTypeId: number): void {
        if (newTypeId === this.selectedDocumentType) {
            return;
        }
        if (this.pendingFiles.length === 0) {
            this.commitDocumentTypeChange(newTypeId);
            return;
        }
        this.confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, { disableClose: false });
        this.confirmDialogRef.componentInstance.confirmMessage =
            'Changing the document type will discard ' + this.pendingFiles.length + ' pending file(s) that have not been submitted. Continue?';
        this.confirmDialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.revokeAllPendingPreviews();
                this.commitDocumentTypeChange(newTypeId);
                return;
            }
            // On cancel the select must be put back by hand. [ngModel] is one-way here, so
            // Angular only writes to the control when the bound value changes - and it did
            // not change - leaving the dropdown showing the type the user just backed out of
            // while uploads still went to the old one. Assigning MatSelect.value re-renders
            // the selection without re-emitting ngModelChange, so this cannot re-enter.
            this.restoreDocumentTypeSelection();
        });
    }

    private restoreDocumentTypeSelection(): void {
        if (this.documentTypeSelect) {
            this.documentTypeSelect.value = this.selectedDocumentType;
        }
    }

    private commitDocumentTypeChange(newTypeId: number): void {
        this.selectedDocumentType = newTypeId;
        this.loadStoredFiles();
    }

    private loadStoredFiles(): void {
        if (!this.selectedDocumentType) {
            return;
        }
        this.loadingFiles = true;
        this.api.listFileMetadata(this.accessToken, this.selectedDocumentType).subscribe(
            files => {
                this.loadingFiles = false;
                this.storedFiles = files;
            },
            err => {
                this.loadingFiles = false;
                // Do not self-refresh here: this *is* the refresh, retrying on its own
                // failure would loop against a persistently failing server.
                this.handleApiError(err, 'Unable to load the stored documents for this type.', false);
            }
        );
    }

    // ---------------------------------------------------------------- Pending file selection

    readFiles(event: Event, fileSrcType: 'captured' | 'uploaded'): void {
        if (event.type === 'click') {
            return;
        }
        const input = event.target as HTMLInputElement;
        const files = input.files;
        if (!files || files.length === 0) {
            return;
        }
        // Reserve slots synchronously. pendingFiles only grows inside the asynchronous
        // FileReader callback below, so checking its length per file (as this used to) sees a
        // stale - usually zero - count and lets an entire multi-select through no matter how
        // many files it holds. inFlightReads covers files already being read but not yet
        // pushed, e.g. a camera capture followed immediately by a second selection.
        const remainingSlots = Math.max(MAX_FILES_PER_BATCH - this.pendingFiles.length - this.inFlightReads, 0);
        const accepted = Math.min(files.length, remainingSlots);
        if (accepted < files.length) {
            // One message for the whole batch rather than one per rejected file.
            this.showMessage('A maximum of ' + MAX_FILES_PER_BATCH + ' files can be submitted at once; '
                + (files.length - accepted) + ' file(s) were not added.');
        }
        for (let i = 0; i < accepted; i++) {
            this.validateAndReadFile(files[i], fileSrcType);
        }
        input.value = '';
    }

    private validateAndReadFile(file: File, fileSrcType: 'captured' | 'uploaded'): void {
        const fileType = (file.type === 'application/pdf') ? 'pdf' : 'jpg';
        if (file.type !== 'application/pdf' && file.type !== 'image/jpeg' && file.type !== 'image/png') {
            this.showMessage('Unsupported file format: ' + file.name);
            return;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            this.showMessage('File too large: ' + file.name);
            return;
        }
        const displayName = (fileSrcType === 'captured') ? this.generateCameraDisplayName() : file.name;
        const previewUrl = URL.createObjectURL(file);

        // Held from here until the file is either pushed or rejected, so a second selection
        // arriving mid-read cannot over-fill the batch. Every exit path below must release it.
        this.inFlightReads++;
        const reject = (message: string) => {
            this.inFlightReads--;
            URL.revokeObjectURL(previewUrl);
            this.showMessage(message);
        };

        const reader = new FileReader();
        reader.onload = e => {
            const rawSrc = reader.result as string;
            const finish = (correctedSrc: string) => {
                const fileSrc = COMMON_UTIL.cleanMetadataFromBase64(correctedSrc, fileType);
                const byteSize = this.base64ByteSize(fileSrc);

                if (byteSize > MAX_FILE_SIZE_BYTES) {
                    reject('File too large: ' + displayName);
                    return;
                }
                const currentTotal = this.pendingFiles.reduce((sum, f) => sum + f.byteSize, 0);
                if (currentTotal + byteSize > MAX_BATCH_SIZE_BYTES) {
                    reject('Submission total size exceeds the ' + (MAX_BATCH_SIZE_BYTES / (1024 * 1024)) + 'MB limit');
                    return;
                }
                const isDuplicate = this.pendingFiles.some(f => f.displayName === displayName && f.byteSize === byteSize);
                if (isDuplicate) {
                    reject('Duplicate file in this submission: ' + displayName);
                    return;
                }
                if (this.pendingFiles.length >= MAX_FILES_PER_BATCH) {
                    // Defensive: readFiles already reserved a slot for this read.
                    reject('A maximum of ' + MAX_FILES_PER_BATCH + ' files can be submitted at once');
                    return;
                }

                this.inFlightReads--;
                this.pendingFiles.push({
                    displayName, fileType, fileSrc, byteSize, previewUrl,
                    safePreviewUrl: this.domSanitizer.bypassSecurityTrustUrl(previewUrl),
                    rawFile: file
                });
            };

            if (fileType === 'jpg') {
                COMMON_UTIL.correctImageOrientation(rawSrc, finish);
            } else {
                finish(rawSrc);
            }
        };
        // Without this a failed read silently drops the file - the user submits a batch
        // missing evidence they believe they attached - and leaks both the object URL and
        // the reserved slot.
        reader.onerror = () => reject('Could not read file: ' + displayName);
        reader.readAsDataURL(file);
    }

    private generateCameraDisplayName(): string {
        return 'Photo_' + new Date().toISOString().replace(/[:.]/g, '-') + '.jpg';
    }

    private base64ByteSize(base64: string): number {
        const padding = base64.endsWith('==') ? 2 : (base64.endsWith('=') ? 1 : 0);
        return Math.floor((base64.length * 3) / 4) - padding;
    }

    removePendingFile(index: number): void {
        const removed = this.pendingFiles.splice(index, 1)[0];
        if (removed) {
            URL.revokeObjectURL(removed.previewUrl);
        }
    }

    private revokeAllPendingPreviews(): void {
        this.pendingFiles.forEach(f => URL.revokeObjectURL(f.previewUrl));
        this.pendingFiles = [];
    }

    // ---------------------------------------------------------------- Submit

    canSubmit(): boolean {
        return !!this.selectedDocumentType && this.pendingFiles.length > 0 && !this.submitting;
    }

    onReset(): void {
        this.revokeAllPendingPreviews();
    }

    onSubmit(): void {
        if (!this.canSubmit()) {
            return;
        }
        this.submitting = true;
        this.spinner.show();
        const files = this.pendingFiles.map(f => ({ displayName: f.displayName, fileType: f.fileType, fileSrc: f.fileSrc }));

        this.api.appendFiles(this.accessToken, this.selectedDocumentType, files).subscribe(
            metadata => {
                this.submitting = false;
                this.spinner.hide();
                this.revokeAllPendingPreviews();
                this.storedFiles = metadata;
                this.confirmDialogRefSession = this.dialog.open(sessionConfirmDialogComponent);
                this.confirmDialogRefSession.componentInstance.confirmMessage = 'Documents Uploaded';
            },
            err => {
                this.submitting = false;
                this.spinner.hide();
                // Pending files are intentionally retained on failure so the user can retry.
                this.handleApiError(err, 'Failed to submit the documents. Please try again.', true);
            }
        );
    }

    // ---------------------------------------------------------------- Stored file actions

    /**
     * Opens a pending file in the same larger preview used for stored files, so a scan can be
     * checked before it is committed (FUNCTIONAL_SPECIFICATION user story 8 / acceptance
     * criterion 2). No network call: the blob is already in the browser.
     */
    previewPendingFile(file: PendingWarehouseFile): void {
        this.previewDialog = this.dialog.open(WarehouseDocumentPreviewDialogComponent);
        this.previewDialog.componentInstance.show(
            file.rawFile,
            file.fileType === 'pdf' ? 'application/pdf' : 'image/jpeg',
            file.displayName);
    }

    previewStoredFile(file: WarehouseFileMetadata): void {
        this.spinner.show();
        const content$ = file.legacyMerged
            ? this.api.getLegacyContent(this.accessToken, this.selectedDocumentType)
            : this.api.getFileContent(this.accessToken, this.selectedDocumentType, file.fileId);

        content$.subscribe(
            content => {
                this.spinner.hide();
                this.previewDialog = this.dialog.open(WarehouseDocumentPreviewDialogComponent);
                this.previewDialog.componentInstance.show(content.blob, content.contentType, file.displayName);
            },
            err => {
                this.spinner.hide();
                this.handleApiError(err, 'Unable to load the document.', true);
            }
        );
    }

    initDeleteStoredFile(file: WarehouseFileMetadata): void {
        this.confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, { disableClose: false });
        this.confirmDialogRef.componentInstance.confirmMessage = 'Are you sure you want to delete ' + file.displayName + '?';
        this.confirmDialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.deleteStoredFile(file);
            }
        });
    }

    private deleteStoredFile(file: WarehouseFileMetadata): void {
        this.spinner.show();
        this.api.deleteFile(this.accessToken, this.selectedDocumentType, file.fileId).subscribe(
            metadata => {
                this.spinner.hide();
                this.storedFiles = metadata;
            },
            err => {
                this.spinner.hide();
                this.handleApiError(err, 'Failed to delete the document.', true);
            }
        );
    }

    // ---------------------------------------------------------------- Error handling

    private handleApiError(err, fallbackMsg: string, refreshOnConflict: boolean): void {
        // 403 as well as 401: JwtTokenUtil raises ForbiddenException (403) for an expired
        // scoped token - both from the JWT exp claim and from the expiry stamped inside the
        // encrypted jti - and reserves UnAuthorisedException (401) for a malformed, wrongly
        // signed or wrong-purpose token. Matching only 401 therefore missed the one case this
        // branch exists for, leaving the user on a dead screen with a generic error.
        if (err && (err.status === 401 || err.status === 403)) {
            this.showMessage('Your session has expired. Please identify the job again.');
            this.backToIdentify();
            return;
        }
        const serverMsg = err && err.error && err.error.errorMsg;
        this.showMessage(serverMsg || fallbackMsg);
        if (refreshOnConflict) {
            this.loadStoredFiles();
        }
    }

    private showMessage(message: string): void {
        this.confirmDialogRefSession = this.dialog.open(sessionConfirmDialogComponent);
        this.confirmDialogRefSession.componentInstance.confirmMessage = message;
    }
}
